import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Helmet } from 'react-helmet';
import axiosConn from '../../axiosConn';
import { AuthContext } from '../../context/AuthContext';
import { useSocket } from '../../context/socket';
import {
  HITLAB_SESSION_MESSAGE,
  buildHitLabAuthConfig,
  getHitLabApiMessage,
  isHitLabClientEnabled,
} from './hitLabUtils';
import HitLabRadioCatalogManager from './HitLabRadioCatalogManager';
import HitLabRadioProgrammingMessageManager from './HitLabRadioProgrammingMessageManager';
import './hitLab.css';

const RIGHTS_SCREEN_STATUS = 'uploaded_pending_rights_screen';

const RIGHTS_SCREEN_NEXT_STATUSES = [
  'ready_for_curator_listen',
  'rejected',
  'hold',
];

const EDITORIAL_STATUSES = [
  'ready_for_curator_listen',
  'rejected',
  'hold',
  'approved_members_rotation',
  'hitlab_candidate',
  'street_drop_candidate',
];

const STATUS_LABELS = {
  uploaded_pending_rights_screen: 'Needs rights screen',
  ready_for_curator_listen: 'Ready for curator listen',
  rejected: 'Rejected',
  hold: 'Hold',
  approved_members_rotation: 'Radio rotation approved',
  hitlab_candidate: 'HitLab candidate',
  street_drop_candidate: 'Street Drop candidate',
};

const FUNNEL_LABELS = {
  hitlab_landing_viewed: 'Landing views',
  hitlab_landing_radio_preview_play_clicked: 'Preview play clicks',
  hitlab_start_artist_intake_clicked: 'Start clicks',
  hitlab_auth_started: 'Auth starts',
  hitlab_intake_viewed: 'Auth completed / intake viewed',
  hitlab_artist_profile_completed: 'Artist profile completed',
  hitlab_checkout_started: 'Checkout started',
  hitlab_checkout_canceled: 'Checkout canceled',
  hitlab_checkout_returned_success: 'Checkout success returned',
  hitlab_payment_confirmed: 'Payment confirmed',
  hitlab_submit_viewed_paid: 'Paid submit viewed',
  hitlab_song_submission_completed: 'Song submissions',
};

const FUNNEL_ORDER = Object.keys(FUNNEL_LABELS);

const LISTEN_RESULT_ELIGIBLE_STATUSES = new Set([
  'uploaded_pending_rights_screen',
  ...EDITORIAL_STATUSES,
]);

const LISTEN_RESULT_DECISION_OPTIONS = [
  ['earned_next_move', 'Earned Next Move'],
  ['not_yet', 'Not Yet'],
  ['hold_second_listen', 'Hold / Second Listen'],
];

const LISTEN_RESULT_PRIMARY_LANE_OPTIONS = [
  ['radio_rotation_candidate', 'Radio Rotation Candidate'],
  ['hit_radar_candidate', 'Hit Radar Candidate'],
  ['needs_stronger_hook', 'Needs stronger hook'],
  ['needs_better_mix_master', 'Needs better mix/master'],
  ['needs_clearer_identity', 'Needs clearer identity'],
  ['needs_stronger_performance', 'Needs stronger performance'],
  ['rights_submission_issue', 'Rights/submission issue'],
  ['not_hitlab_fit', 'Not a HitLab fit'],
];

const LISTEN_RESULT_CTA_OPTIONS = [
  ['watch_for_radio_launch', 'Watch for Joe Galaxy Radio launch'],
  ['confirm_rotation_readiness', 'Confirm Rotation Readiness'],
  ['come_back_with_stronger_record', 'Come back with a stronger record'],
  ['localops_earning_lane', 'JG LocalOps earning lane'],
  ['hold_for_second_listen', 'Hold for second listen'],
];

const LISTEN_RESULT_SCORE_FIELDS = [
  ['hook', 'Hook'],
  ['artistIdentity', 'Artist identity'],
  ['vocalPerformance', 'Vocal / performance'],
  ['productionMix', 'Production / mix'],
  ['replayValue', 'Replay value'],
  ['marketEnergy', 'Market energy'],
  ['fanActionPotential', 'Fan-action potential'],
  ['jgUniverseFit', 'JG Universe fit'],
];

const INITIAL_LISTEN_RESULT_FORM = {
  decision: '',
  primaryLane: '',
  privateLinkWorks: '',
  rightsClearForReview: '',
  scores: LISTEN_RESULT_SCORE_FIELDS.reduce((acc, [key]) => {
    acc[key] = '';
    return acc;
  }, {}),
  bestMomentTimestamp: '',
  strongestThing: '',
  mainLimiter: '',
  oneSentenceTruth: '',
  recommendedNextAction: '',
  ctaType: '',
  internalNotes: '',
};

const INITIAL_RECOVERY_FORM = {
  hoursOld: '1',
  maxAgeDays: '14',
  limit: '',
  testEmail: '',
  intakeIds: '',
};

const parseRecoveryIntakeIds = (value = '') =>
  Array.from(
    new Set(
      String(value || '')
        .split(/[\n,]+/)
        .map((entry) => entry.trim())
        .filter(Boolean),
    ),
  );

const buildRecoveryRequestPayload = (form, { dryRun = false } = {}) => {
  const payload = {
    dryRun,
    hoursOld: Number(form.hoursOld) || 1,
    maxAgeDays: Number(form.maxAgeDays) || 14,
  };
  const intakeIds = parseRecoveryIntakeIds(form.intakeIds);
  if (intakeIds.length) payload.intakeIds = intakeIds;
  const limit = Number(form.limit);
  if (Number.isFinite(limit) && limit > 0) {
    payload.limit = Math.floor(limit);
  }
  return payload;
};

const getRecoveryRunSummary = (data = {}) => {
  if (!data) return '';
  const sent = Number(data.sentCount || 0);
  const eligible = Number(data.eligibleCount || 0);
  const failed = Number(data.failedCount || 0);
  const selected = Number(data.selectedCount || 0);
  if (data.dryRun) {
    return `${eligible} eligible, ${selected} selected, ${Number(
      data.skippedCount || 0,
    )} skipped.`;
  }
  return `${sent} sent, ${failed} failed, ${selected} selected.`;
};

const getStatusLabel = (status) =>
  STATUS_LABELS[status] ||
  String(status || 'unknown')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const normalizeStatus = (status) => String(status || '').trim() || 'unknown';

const getAllowedAdminStatusOptions = (currentStatus) => {
  const normalizedStatus = normalizeStatus(currentStatus);
  const statuses = new Set([normalizedStatus]);
  if (normalizedStatus === RIGHTS_SCREEN_STATUS) {
    RIGHTS_SCREEN_NEXT_STATUSES.forEach((status) => statuses.add(status));
    return Array.from(statuses);
  }
  if (EDITORIAL_STATUSES.includes(normalizedStatus)) {
    EDITORIAL_STATUSES.forEach((status) => statuses.add(status));
  }
  return Array.from(statuses);
};

const isStatusSelectDisabled = (currentStatus) =>
  getAllowedAdminStatusOptions(currentStatus).length < 2;

const HITLAB_TRANSITION_STALE_MESSAGE =
  'Status could not be updated because that transition is no longer allowed. Refresh and try again.';

const getPlayerRecord = (player) => player?.data?.player || player?.player || player || null;

const canOpenListenResult = (row) =>
  row?.paymentStatus === 'paid' &&
  !!row?.songPrivateUrl &&
  LISTEN_RESULT_ELIGIBLE_STATUSES.has(normalizeStatus(row?.status));

const getListenResultFormFromPayload = (listenResult = {}) => ({
  ...INITIAL_LISTEN_RESULT_FORM,
  decision: listenResult.decision || '',
  primaryLane: listenResult.primaryLane || '',
  privateLinkWorks: listenResult.privateLinkWorks || '',
  rightsClearForReview: listenResult.rightsClearForReview || '',
  scores: LISTEN_RESULT_SCORE_FIELDS.reduce((acc, [key]) => {
    acc[key] = listenResult.scores?.[key] ?? '';
    return acc;
  }, {}),
  bestMomentTimestamp: listenResult.bestMomentTimestamp || '',
  strongestThing: listenResult.strongestThing || '',
  mainLimiter: listenResult.mainLimiter || '',
  oneSentenceTruth: listenResult.oneSentenceTruth || '',
  recommendedNextAction: listenResult.recommendedNextAction || '',
  ctaType: listenResult.ctaType || '',
  internalNotes: listenResult.internalNotes || '',
});

export default function HitLabAdmin() {
  const { player } = useContext(AuthContext);
  const { socket } = useSocket() || {};
  const record = getPlayerRecord(player);
  const isAdmin = String(record?.role || '').trim() === 'admin';
  const authConfig = useMemo(
    () => buildHitLabAuthConfig(player),
    [player?.token],
  );
  const [rows, setRows] = useState([]);
  const [funnel, setFunnel] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshPromptVisible, setRefreshPromptVisible] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [listenPanelOpen, setListenPanelOpen] = useState(false);
  const [listenResultRow, setListenResultRow] = useState(null);
  const [listenForm, setListenForm] = useState(INITIAL_LISTEN_RESULT_FORM);
  const [listenDraft, setListenDraft] = useState({ subject: '', body: '' });
  const [listenLoading, setListenLoading] = useState(false);
  const [listenSaving, setListenSaving] = useState(false);
  const [listenError, setListenError] = useState('');
  const [listenMessage, setListenMessage] = useState('');
  const [recoverySummary, setRecoverySummary] = useState(null);
  const [recoveryForm, setRecoveryForm] = useState(INITIAL_RECOVERY_FORM);
  const [recoveryResult, setRecoveryResult] = useState('');
  const [recoveryError, setRecoveryError] = useState('');
  const [recoveryBusy, setRecoveryBusy] = useState(false);
  const focusRefreshTimerRef = useRef(null);
  const activeStatusEditRef = useRef(false);
  const statusUpdatingRef = useRef(false);
  const pendingInvalidationWhileHiddenRef = useRef(false);
  const clientEnabled = isHitLabClientEnabled();

  const hasActiveStatusWork = useCallback(
    () => activeStatusEditRef.current || statusUpdatingRef.current,
    [],
  );

  const loadRows = useCallback(() => {
    if (!authConfig) {
      setError(HITLAB_SESSION_MESSAGE);
      return Promise.resolve();
    }
    return axiosConn.get('hitlab/admin/intakes', authConfig).then((response) => {
      setRows(response.data?.data || []);
    });
  }, [authConfig]);

  const loadFunnel = useCallback(() => {
    if (!authConfig) return Promise.resolve();
    return axiosConn.get('hitlab/admin/funnel', authConfig).then((response) => {
      setFunnel(response.data?.data || null);
    });
  }, [authConfig]);

  const loadRecoverySummary = useCallback(() => {
    if (!authConfig) return Promise.resolve();
    return axiosConn
      .get('hitlab/admin/recovery/pending-payment-email/summary', authConfig)
      .then((response) => {
        setRecoverySummary(response.data?.data || null);
      });
  }, [authConfig]);

  const refreshAdminData = useCallback(
    async (reason = 'manual refresh') => {
      if (!authConfig) {
        setError(HITLAB_SESSION_MESSAGE);
        return;
      }
      setError('');
      try {
        await loadRows();
        await loadFunnel().catch(() => {
          // Funnel summary is useful but must not block the admin queue.
        });
        await loadRecoverySummary().catch(() => {
          // Recovery summary is operational context and should not block queue work.
        });
        pendingInvalidationWhileHiddenRef.current = false;
        setRefreshPromptVisible(false);
      } catch (err) {
        setError(getHitLabApiMessage(err, 'HitLab admin queue could not be loaded.'));
      }
    },
    [authConfig, loadFunnel, loadRecoverySummary, loadRows],
  );

  useEffect(() => {
    if (!clientEnabled || !isAdmin) {
      setLoading(false);
      return undefined;
    }
    let active = true;
    refreshAdminData('initial load').finally(() => {
      if (active) setLoading(false);
    });
    return () => {
      active = false;
    };
  }, [clientEnabled, isAdmin, refreshAdminData]);

  useEffect(() => {
    if (!clientEnabled || !isAdmin || !authConfig || !socket?.on || !socket?.off || !socket?.emit) {
      return undefined;
    }

    let active = true;
    const subscribe = () => {
      if (!socket.connected) return;
      socket.emit('hitlab:admin:subscribe', {}, () => {});
    };
    const handleInvalidated = () => {
      if (!active) return;
      setRefreshPromptVisible(true);
      if (typeof document !== 'undefined' && document.hidden) {
        pendingInvalidationWhileHiddenRef.current = true;
      }
    };
    const handleConnect = () => subscribe();

    socket.on('connect', handleConnect);
    socket.on('hitlab:admin:invalidated', handleInvalidated);
    subscribe();

    return () => {
      active = false;
      socket.off('connect', handleConnect);
      socket.off('hitlab:admin:invalidated', handleInvalidated);
    };
  }, [authConfig, clientEnabled, isAdmin, socket]);

  useEffect(() => {
    if (!clientEnabled || !isAdmin || !authConfig) return undefined;
    const scheduleRefresh = (reason = 'tab focus') => {
      if (typeof document !== 'undefined' && document.hidden) return;
      window.clearTimeout(focusRefreshTimerRef.current);
      focusRefreshTimerRef.current = window.setTimeout(() => {
        if (hasActiveStatusWork()) {
          setRefreshPromptVisible(true);
          return;
        }
        void refreshAdminData(reason);
      }, 900);
    };
    const handleVisibilityChange = () => {
      if (document.hidden) return;
      scheduleRefresh(
        pendingInvalidationWhileHiddenRef.current
          ? 'hidden socket invalidation'
          : 'tab focus',
      );
    };
    const handleWindowFocus = () => {
      scheduleRefresh(
        pendingInvalidationWhileHiddenRef.current
          ? 'hidden socket invalidation'
          : 'tab focus',
      );
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleWindowFocus);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleWindowFocus);
      window.clearTimeout(focusRefreshTimerRef.current);
    };
  }, [
    authConfig,
    clientEnabled,
    hasActiveStatusWork,
    isAdmin,
    refreshAdminData,
  ]);

  const updateStatus = async (row, status) => {
    const currentStatus = normalizeStatus(row?.status);
    if (status === currentStatus) return;
    setError('');
    if (!authConfig) {
      setError(HITLAB_SESSION_MESSAGE);
      return;
    }
    statusUpdatingRef.current = true;
    setStatusUpdating(true);
    try {
      await axiosConn.patch(`hitlab/admin/intakes/${row.id}/status`, {
        status,
      }, authConfig);
      await refreshAdminData('status update');
    } catch (err) {
      if (Number(err?.response?.status) === 400) {
        setError(HITLAB_TRANSITION_STALE_MESSAGE);
        return;
      }
      setError(getHitLabApiMessage(err, 'Status could not be updated.'));
    } finally {
      statusUpdatingRef.current = false;
      setStatusUpdating(false);
    }
  };

  const openListenResult = async (row) => {
    if (!canOpenListenResult(row)) return;
    setListenPanelOpen(true);
    setListenResultRow(row);
    setListenForm(INITIAL_LISTEN_RESULT_FORM);
    setListenDraft({ subject: '', body: '' });
    setListenError('');
    setListenMessage('');
    if (!authConfig) {
      setListenError(HITLAB_SESSION_MESSAGE);
      return;
    }
    setListenLoading(true);
    try {
      const response = await axiosConn.get(
        `hitlab/admin/intakes/${row.id}/listen-result`,
        authConfig,
      );
      const listenResult = response.data?.data?.listenResult || {};
      setListenForm(getListenResultFormFromPayload(listenResult));
      setListenDraft({
        subject: listenResult.emailSubjectDraft || '',
        body: listenResult.emailBodyDraft || '',
      });
    } catch (err) {
      setListenError(
        getHitLabApiMessage(err, 'Listen Result could not be loaded.'),
      );
    } finally {
      setListenLoading(false);
    }
  };

  const closeListenResult = () => {
    setListenPanelOpen(false);
    setListenResultRow(null);
    setListenForm(INITIAL_LISTEN_RESULT_FORM);
    setListenDraft({ subject: '', body: '' });
    setListenError('');
    setListenMessage('');
  };

  const updateListenField = (event) => {
    const { name, value } = event.target;
    setListenForm((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const updateListenScore = (key, value) => {
    setListenForm((current) => ({
      ...current,
      scores: {
        ...current.scores,
        [key]: value,
      },
    }));
  };

  const generateListenDraft = async (event) => {
    event.preventDefault();
    setListenError('');
    setListenMessage('');
    if (!authConfig) {
      setListenError(HITLAB_SESSION_MESSAGE);
      return;
    }
    if (!listenResultRow?.id) {
      setListenError('Choose a HitLab intake before generating a Listen Result.');
      return;
    }
    setListenSaving(true);
    try {
      const response = await axiosConn.post(
        `hitlab/admin/intakes/${listenResultRow.id}/listen-result/draft`,
        listenForm,
        authConfig,
      );
      const listenResult = response.data?.data?.listenResult || {};
      setListenForm(getListenResultFormFromPayload(listenResult));
      setListenDraft({
        subject: listenResult.emailSubjectDraft || '',
        body: listenResult.emailBodyDraft || '',
      });
      setListenMessage('Listen Result draft saved.');
    } catch (err) {
      setListenError(
        getHitLabApiMessage(err, 'Listen Result draft could not be generated.'),
      );
    } finally {
      setListenSaving(false);
    }
  };

  const copyListenDraft = async () => {
    setListenMessage('');
    setListenError('');
    const copyText = [
      listenDraft.subject ? `Subject: ${listenDraft.subject}` : '',
      listenDraft.body,
    ].filter(Boolean).join('\n\n');
    if (!copyText.trim()) {
      setListenError('Generate a Listen Result draft before copying.');
      return;
    }
    try {
      if (!navigator.clipboard?.writeText) {
        setListenMessage('Select the draft text and copy it manually.');
        return;
      }
      await navigator.clipboard.writeText(copyText);
      setListenMessage('Email draft copied.');
    } catch (err) {
      setListenMessage('Select the draft text and copy it manually.');
    }
  };

  const updateRecoveryField = (event) => {
    const { name, value } = event.target;
    setRecoveryForm((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const runRecoveryDryRun = async () => {
    setRecoveryError('');
    setRecoveryResult('');
    if (!authConfig) {
      setRecoveryError(HITLAB_SESSION_MESSAGE);
      return;
    }
    setRecoveryBusy(true);
    try {
      const payload = buildRecoveryRequestPayload(recoveryForm, { dryRun: true });
      const response = await axiosConn.post(
        'hitlab/admin/recovery/pending-payment-email/run',
        payload,
        authConfig,
      );
      const data = response.data?.data || {};
      setRecoveryResult(`Dry run: ${getRecoveryRunSummary(data)}`);
      setRecoverySummary((current) => ({
        ...(current || {}),
        ...data,
      }));
    } catch (err) {
      setRecoveryError(
        getHitLabApiMessage(err, 'Recovery dry run could not be completed.'),
      );
    } finally {
      setRecoveryBusy(false);
    }
  };

  const runRecoverySend = async () => {
    setRecoveryError('');
    setRecoveryResult('');
    if (!authConfig) {
      setRecoveryError(HITLAB_SESSION_MESSAGE);
      return;
    }
    const payload = buildRecoveryRequestPayload(recoveryForm, { dryRun: false });
    const targetedCount = Array.isArray(payload.intakeIds)
      ? payload.intakeIds.length
      : 0;
    const confirmCopy = payload.limit || targetedCount
      ? `Send HitLab recovery email to ${payload.limit || targetedCount} selected eligible intake(s)?`
      : 'Send HitLab recovery email to all currently eligible pending-payment intakes?';
    if (!window.confirm(confirmCopy)) return;

    setRecoveryBusy(true);
    try {
      const response = await axiosConn.post(
        'hitlab/admin/recovery/pending-payment-email/run',
        payload,
        authConfig,
      );
      const data = response.data?.data || {};
      setRecoveryResult(`Run complete: ${getRecoveryRunSummary(data)}`);
      await loadRecoverySummary().catch(() => {});
    } catch (err) {
      setRecoveryError(
        getHitLabApiMessage(err, 'Recovery email run could not be completed.'),
      );
    } finally {
      setRecoveryBusy(false);
    }
  };

  const sendRecoveryTestEmail = async () => {
    setRecoveryError('');
    setRecoveryResult('');
    if (!authConfig) {
      setRecoveryError(HITLAB_SESSION_MESSAGE);
      return;
    }
    if (!String(recoveryForm.testEmail || '').trim()) {
      setRecoveryError('Test email is required.');
      return;
    }
    setRecoveryBusy(true);
    try {
      const response = await axiosConn.post(
        'hitlab/admin/recovery/pending-payment-email/test-send',
        { testEmail: recoveryForm.testEmail },
        authConfig,
      );
      const warning = response.data?.data?.warning;
      setRecoveryResult(
        warning
          ? `Test sent. ${warning}`
          : `Test sent to ${recoveryForm.testEmail}.`,
      );
    } catch (err) {
      setRecoveryError(
        getHitLabApiMessage(err, 'Recovery test email could not be sent.'),
      );
    } finally {
      setRecoveryBusy(false);
    }
  };

  if (!isAdmin) {
    return (
      <main className="hitlab-page hitlab-admin">
        <section className="hitlab-shell">
          <h1>HitLab admin unavailable</h1>
          <p>This queue is restricted to admins.</p>
        </section>
      </main>
    );
  }

  return (
    <main className="hitlab-page hitlab-admin">
      <Helmet>
        <title>JG HitLab Admin Queue</title>
      </Helmet>
      <section className="hitlab-shell">
        <p className="hitlab-kicker">ADMIN QUEUE</p>
        <h1>JG HitLab Artist Intakes</h1>
        {error ? <div className="hitlab-alert hitlab-alert-error">{error}</div> : null}
        {refreshPromptVisible ? (
          <div className="hitlab-alert hitlab-admin-refresh-prompt">
            <span>HitLab admin has new activity.</span>
            <button
              type="button"
              className="hitlab-secondary-btn"
              disabled={statusUpdating}
              onClick={() => refreshAdminData('manual refresh')}
            >
              Refresh queue
            </button>
          </div>
        ) : null}
        {!clientEnabled ? (
          <div className="hitlab-alert">Artist intake is disabled.</div>
        ) : loading ? (
          <div className="hitlab-alert">Loading queue...</div>
        ) : !authConfig ? (
          null
        ) : (
          <>
            <section className="hitlab-admin-funnel" aria-label="HitLab funnel summary">
              <article className="hitlab-admin-funnel-card hitlab-admin-funnel-card-accent">
                <span>Needs review</span>
                <strong>{Number(funnel?.needsReviewCount || 0)}</strong>
              </article>
              {FUNNEL_ORDER.map((eventName) => (
                <article className="hitlab-admin-funnel-card" key={eventName}>
                  <span>{FUNNEL_LABELS[eventName]}</span>
                  <strong>{Number(funnel?.windows?.allTime?.[eventName] || 0)}</strong>
                  <small>All time</small>
                </article>
              ))}
            </section>

            <section className="hitlab-admin-status-strip" aria-label="HitLab status counts">
              {Object.entries(STATUS_LABELS).map(([status, label]) => (
                <article key={status}>
                  <span>{label}</span>
                  <strong>{Number(funnel?.statusCounts?.[status] || 0)}</strong>
                </article>
              ))}
            </section>

            <section
              className="hitlab-recovery-admin"
              aria-label="HitLab pending payment recovery email"
            >
              <div className="hitlab-recovery-admin-header">
                <div>
                  <p className="hitlab-kicker">PENDING PAYMENT RECOVERY</p>
                  <h2>Profile-ready recovery email</h2>
                  <p>
                    {Number(recoverySummary?.eligibleCount || 0)} eligible now -{' '}
                    {Number(recoverySummary?.sentCount || 0)} sent
                  </p>
                </div>
                <button
                  type="button"
                  className="hitlab-secondary-btn"
                  disabled={recoveryBusy}
                  onClick={loadRecoverySummary}
                >
                  Refresh summary
                </button>
              </div>

              {recoveryError ? (
                <div className="hitlab-alert hitlab-alert-error">{recoveryError}</div>
              ) : null}
              {recoveryResult ? (
                <div className="hitlab-alert hitlab-alert-success">{recoveryResult}</div>
              ) : null}

              <div className="hitlab-recovery-metrics">
                <article>
                  <span>Eligible</span>
                  <strong>{Number(recoverySummary?.eligibleCount || 0)}</strong>
                </article>
                <article>
                  <span>Skipped</span>
                  <strong>{Number(recoverySummary?.skippedCount || 0)}</strong>
                </article>
                <article>
                  <span>Failed</span>
                  <strong>{Number(recoverySummary?.failedCount || 0)}</strong>
                </article>
                <article>
                  <span>Address</span>
                  <strong>
                    {recoverySummary?.postalAddress?.configured ? 'Ready' : 'Blocked'}
                  </strong>
                </article>
              </div>

              <div className="hitlab-recovery-grid">
                <div className="hitlab-recovery-controls">
                  <label>
                    Hours old
                    <input
                      type="number"
                      name="hoursOld"
                      min="1"
                      step="1"
                      value={recoveryForm.hoursOld}
                      onChange={updateRecoveryField}
                    />
                  </label>
                  <label>
                    Max age days
                    <input
                      type="number"
                      name="maxAgeDays"
                      min="1"
                      step="1"
                      value={recoveryForm.maxAgeDays}
                      onChange={updateRecoveryField}
                    />
                  </label>
                  <label>
                    Optional safety limit
                    <input
                      type="number"
                      name="limit"
                      min="1"
                      step="1"
                      placeholder="All eligible"
                      value={recoveryForm.limit}
                      onChange={updateRecoveryField}
                    />
                  </label>
                  <label>
                    Test email
                    <input
                      type="email"
                      name="testEmail"
                      placeholder="admin@example.com"
                      value={recoveryForm.testEmail}
                      onChange={updateRecoveryField}
                    />
                  </label>
                  <label className="hitlab-form-wide">
                    Targeted intake IDs
                    <textarea
                      name="intakeIds"
                      placeholder="Comma or newline separated"
                      value={recoveryForm.intakeIds}
                      onChange={updateRecoveryField}
                    />
                  </label>
                </div>

                <div className="hitlab-recovery-actions">
                  <button
                    type="button"
                    className="hitlab-secondary-btn"
                    disabled={recoveryBusy}
                    onClick={runRecoveryDryRun}
                  >
                    Dry run
                  </button>
                  <button
                    type="button"
                    className="hitlab-secondary-btn"
                    disabled={recoveryBusy}
                    onClick={sendRecoveryTestEmail}
                  >
                    Send test
                  </button>
                  <button
                    type="button"
                    className="hitlab-primary-btn"
                    disabled={recoveryBusy}
                    onClick={runRecoverySend}
                  >
                    {recoveryBusy ? 'Working...' : 'Run recovery'}
                  </button>
                  {!recoveryForm.limit && !parseRecoveryIntakeIds(recoveryForm.intakeIds).length ? (
                    <p>Run recovery sends to all currently eligible intakes.</p>
                  ) : null}
                </div>
              </div>
            </section>

            <HitLabRadioProgrammingMessageManager authConfig={authConfig} />
            <HitLabRadioCatalogManager authConfig={authConfig} />

            <div className="hitlab-table-wrap">
              <table className="hitlab-table">
                <thead>
                  <tr>
                    <th>Created</th>
                    <th>Artist</th>
                    <th>Song</th>
                    <th>Genre</th>
                    <th>Paid</th>
                    <th>Rights</th>
                    <th>Listen</th>
                    <th>Result</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const currentStatus = normalizeStatus(row.status);
                    const statusOptions = getAllowedAdminStatusOptions(currentStatus);
                    const statusSelectDisabled = isStatusSelectDisabled(currentStatus);
                    return (
                      <tr key={row.id}>
                        <td>{row.createdAt ? new Date(row.createdAt).toLocaleString() : '--'}</td>
                        <td>
                          <strong>{row.artistName || 'Unsubmitted artist'}</strong>
                          <span>{row.cityRegion || 'No city yet'}</span>
                        </td>
                        <td>{row.songTitle || 'No song yet'}</td>
                        <td>{row.genreLane || '--'}</td>
                        <td>{row.paymentStatus === 'paid' ? 'Paid' : 'Pending'}</td>
                        <td>
                          {row.rights?.required?.privateReviewPlayback
                            ? 'Confirmed'
                            : 'Pending'}
                        </td>
                        <td>
                          {row.songPrivateUrl ? (
                            <a
                              href={row.songPrivateUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Open
                            </a>
                          ) : (
                            '--'
                          )}
                        </td>
                        <td>
                          {canOpenListenResult(row) ? (
                            <>
                              <button
                                type="button"
                                className="hitlab-table-action"
                                onClick={() => openListenResult(row)}
                              >
                                Open Listen Result
                              </button>
                              {row.listenResult?.status === 'drafted' ? (
                                <span>Drafted</span>
                              ) : null}
                            </>
                          ) : (
                            '--'
                          )}
                        </td>
                        <td>
                          <select
                            className="hitlab-status-select"
                            aria-label={`Status for ${row.artistName || row.id}`}
                            disabled={statusSelectDisabled}
                            value={currentStatus}
                            onFocus={() => {
                              activeStatusEditRef.current = true;
                            }}
                            onBlur={() => {
                              activeStatusEditRef.current = false;
                            }}
                            onChange={(event) => updateStatus(row, event.target.value)}
                          >
                            {statusOptions.map((status) => (
                              <option key={status} value={status}>
                                {getStatusLabel(status)}
                              </option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    );
                  })}
                  {!rows.length ? (
                    <tr>
                      <td colSpan="9">No HitLab intakes yet.</td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
            {listenPanelOpen ? (
              <section className="hitlab-listen-panel" aria-label="JG HitLab Listen Result">
                <div className="hitlab-listen-panel-header">
                  <div>
                    <p className="hitlab-kicker">JG HITLAB LISTEN RESULT</p>
                    <h2>{listenResultRow?.artistName || 'HitLab artist'}</h2>
                    <p>
                      {listenResultRow?.songTitle || 'Untitled song'} -{' '}
                      {getStatusLabel(listenResultRow?.status)}
                    </p>
                  </div>
                  <button
                    type="button"
                    className="hitlab-secondary-btn"
                    onClick={closeListenResult}
                  >
                    Close
                  </button>
                </div>
                {listenError ? (
                  <div className="hitlab-alert hitlab-alert-error">{listenError}</div>
                ) : null}
                {listenMessage ? (
                  <div className="hitlab-alert hitlab-alert-success">{listenMessage}</div>
                ) : null}
                {listenLoading ? (
                  <div className="hitlab-alert">Loading Listen Result...</div>
                ) : (
                  <form className="hitlab-listen-form" onSubmit={generateListenDraft}>
                    <div className="hitlab-listen-form-grid">
                      <label>
                        Private link works?
                        <select
                          name="privateLinkWorks"
                          value={listenForm.privateLinkWorks}
                          onChange={updateListenField}
                          required
                        >
                          <option value="">Select</option>
                          <option value="yes">Yes</option>
                          <option value="no">No</option>
                        </select>
                      </label>
                      <label>
                        Rights clear enough for review?
                        <select
                          name="rightsClearForReview"
                          value={listenForm.rightsClearForReview}
                          onChange={updateListenField}
                          required
                        >
                          <option value="">Select</option>
                          <option value="yes">Yes</option>
                          <option value="no">No</option>
                          <option value="concern">Concern</option>
                        </select>
                      </label>
                      <label>
                        Decision
                        <select
                          name="decision"
                          value={listenForm.decision}
                          onChange={updateListenField}
                          required
                        >
                          <option value="">Select</option>
                          {LISTEN_RESULT_DECISION_OPTIONS.map(([value, label]) => (
                            <option key={value} value={value}>{label}</option>
                          ))}
                        </select>
                      </label>
                      <label>
                        Primary lane
                        <select
                          name="primaryLane"
                          value={listenForm.primaryLane}
                          onChange={updateListenField}
                        >
                          <option value="">Backend default</option>
                          {LISTEN_RESULT_PRIMARY_LANE_OPTIONS.map(([value, label]) => (
                            <option key={value} value={value}>{label}</option>
                          ))}
                        </select>
                      </label>
                      <label>
                        CTA type
                        <select
                          name="ctaType"
                          value={listenForm.ctaType}
                          onChange={updateListenField}
                        >
                          <option value="">Backend default</option>
                          {LISTEN_RESULT_CTA_OPTIONS.map(([value, label]) => (
                            <option key={value} value={value}>{label}</option>
                          ))}
                        </select>
                      </label>
                    </div>

                    <fieldset className="hitlab-listen-scores">
                      <legend>Scores, 1-10</legend>
                      <div className="hitlab-listen-score-grid">
                        {LISTEN_RESULT_SCORE_FIELDS.map(([key, label]) => (
                          <label key={key}>
                            {label}
                            <input
                              type="number"
                              min="1"
                              max="10"
                              step="1"
                              value={listenForm.scores[key]}
                              onChange={(event) => updateListenScore(key, event.target.value)}
                            />
                          </label>
                        ))}
                      </div>
                    </fieldset>

                    <div className="hitlab-listen-form-grid">
                      <label>
                        Best moment / timestamp
                        <input
                          name="bestMomentTimestamp"
                          maxLength="80"
                          value={listenForm.bestMomentTimestamp}
                          onChange={updateListenField}
                        />
                      </label>
                      <label>
                        One sentence truth
                        <input
                          name="oneSentenceTruth"
                          maxLength="500"
                          value={listenForm.oneSentenceTruth}
                          onChange={updateListenField}
                        />
                      </label>
                      <label className="hitlab-form-wide">
                        Strongest thing about the record
                        <textarea
                          name="strongestThing"
                          maxLength="1000"
                          value={listenForm.strongestThing}
                          onChange={updateListenField}
                        />
                      </label>
                      <label className="hitlab-form-wide">
                        Main thing holding it back
                        <textarea
                          name="mainLimiter"
                          maxLength="1000"
                          value={listenForm.mainLimiter}
                          onChange={updateListenField}
                        />
                      </label>
                      <label className="hitlab-form-wide">
                        Recommended next action
                        <textarea
                          name="recommendedNextAction"
                          maxLength="1000"
                          value={listenForm.recommendedNextAction}
                          onChange={updateListenField}
                        />
                      </label>
                      <label className="hitlab-form-wide">
                        Internal notes, not sent to artist
                        <textarea
                          name="internalNotes"
                          maxLength="2000"
                          value={listenForm.internalNotes}
                          onChange={updateListenField}
                        />
                      </label>
                    </div>

                    <div className="hitlab-listen-actions">
                      <button
                        type="submit"
                        className="hitlab-primary-btn"
                        disabled={listenSaving}
                      >
                        {listenSaving ? 'Generating...' : 'Generate Listen Result Draft'}
                      </button>
                      <button
                        type="button"
                        className="hitlab-secondary-btn"
                        onClick={copyListenDraft}
                        disabled={!listenDraft.body}
                      >
                        Copy Email Draft
                      </button>
                    </div>
                  </form>
                )}

                {listenDraft.subject || listenDraft.body ? (
                  <div className="hitlab-listen-draft">
                    <label>
                      Email subject draft
                      <input
                        value={listenDraft.subject}
                        onChange={(event) =>
                          setListenDraft((current) => ({
                            ...current,
                            subject: event.target.value,
                          }))
                        }
                      />
                    </label>
                    <label>
                      Email body draft
                      <textarea
                        value={listenDraft.body}
                        onChange={(event) =>
                          setListenDraft((current) => ({
                            ...current,
                            body: event.target.value,
                          }))
                        }
                        onFocus={(event) => event.target.select()}
                      />
                    </label>
                  </div>
                ) : null}
              </section>
            ) : null}
          </>
        )}
      </section>
    </main>
  );
}
